import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, PackageSearch, AlertTriangle, RotateCw, Clock } from "lucide-react";
import { Timeline } from "@/components/rastreo/Timeline";
import { Gallery } from "@/components/rastreo/Gallery";
import { VideoItem } from "@/components/rastreo/VideoItem";
import { StageBadge } from "@/components/rastreo/StageBadge";
import { UnidadDetalleSkeleton } from "@/components/rastreo/UnidadDetalleSkeleton";
import { TipoIcon } from "@/components/rastreo/TipoIcon";
import { portalApi } from "@/lib/portalAuth";
import { clasificarError, mensajeError } from "@/lib/api";
import { formatFecha, nombreTipo, iconoTipo } from "@/lib/format";
import { usePageTitle } from "@/lib/usePageTitle";
import { RASTREO } from "@/constants/testIds";

export default function PortalUnidadDetalle() {
  const { vin } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["portal-unidad", vin],
    queryFn: () => portalApi.get(`/portal/unidades/${encodeURIComponent(vin)}`).then((r) => r.data),
  });
  const { data: tipos = [] } = useQuery({
    queryKey: ["tipos"],
    queryFn: () => portalApi.get("/tipos").then((r) => r.data),
  });


  usePageTitle(data ? `${data.unidad.marca} ${data.unidad.modelo} · ${data.unidad.vin}` : "Mi unidad");

  if (isLoading) {
    return (
      <main className="mx-auto max-w-5xl px-5 py-6 sm:py-8">
        <UnidadDetalleSkeleton />
      </main>
    );
  }

  if (isError) {
    const tipo = clasificarError(error);
    if (tipo === "limite") {
      return (
        <main className="mx-auto max-w-2xl px-5 py-20">
          <div data-testid={RASTREO.rateLimitError} className="rounded-[var(--tc-radius)] border border-amber-200 bg-amber-50 p-8 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white text-amber-600">
              <Clock size={26} />
            </span>
            <h2 className="mt-4 text-lg font-semibold text-[var(--tc-text)]">Estás consultando muy rápido</h2>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-[var(--tc-text-secondary)]">
              {mensajeError(error, "Estás realizando muchas consultas. Esperá unos minutos e intentá de nuevo.")}
            </p>
          </div>
        </main>
      );
    }
    const esRed = tipo === "servicio";
    return (
      <main className="mx-auto max-w-2xl px-5 py-20">
        <div className={`rounded-[var(--tc-radius)] border p-8 text-center ${esRed ? "border-red-200 bg-red-50" : "border-[var(--tc-border)] bg-white"}`}>
          <span className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${esRed ? "bg-white text-red-500" : "bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]"}`}>
            {esRed ? <AlertTriangle size={26} /> : <PackageSearch size={26} />}
          </span>
          <h2 className="mt-4 text-lg font-semibold text-[var(--tc-text)]">
            {esRed ? "Hubo un problema para cargar esta unidad" : "No encontramos esa unidad"}
          </h2>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-[var(--tc-text-secondary)]">
            {esRed
              ? "No es que la unidad no exista: el servicio tuvo un problema. Intentá de nuevo."
              : "Puede que ya no esté disponible. Volvé a tu listado de unidades."}
          </p>
          {esRed ? (
            <button
              onClick={() => refetch()}
              className="mt-6 inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)]"
            >
              <RotateCw size={16} /> Reintentar
            </button>
          ) : (
            <button
              onClick={() => navigate("/portal/unidades")}
              className="mt-6 inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)]"
            >
              <ArrowLeft size={16} /> Volver a mis unidades
            </button>
          )}
        </div>
      </main>
    );
  }

  const { unidad, etapa_actual, timeline, fotos, videos } = data;

  return (
    <main className="mx-auto max-w-5xl px-5 py-6 sm:py-8">
      <button
        onClick={() => navigate("/portal/unidades")}
        className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--tc-text-secondary)] transition-colors hover:text-[var(--tc-accent)]"
      >
        <ArrowLeft size={16} /> Mis unidades
      </button>

      <div data-testid={RASTREO.detailHeader} className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6 tc-fade-up">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[var(--tc-radius)] bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]">
              <TipoIcon icono={iconoTipo(tipos, unidad.tipo)} size={24} />
            </span>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-[var(--tc-text)] sm:text-2xl">
                {unidad.marca} {unidad.modelo}
              </h1>
              <p data-testid={RASTREO.detailVin} className="mt-1 font-mono text-sm tabular-nums text-[var(--tc-text-secondary)]">{unidad.vin}</p>
            </div>
          </div>
          <StageBadge etapa={etapa_actual} />
        </div>
      </div>

      <Panel titulo="Estado del viaje" className="mt-5">
        <div className="pt-2">
          <Timeline timeline={timeline} />
        </div>
      </Panel>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Panel titulo="Ficha del vehículo" testId={RASTREO.vehicleCard}>
          <Datos
            filas={[
              ["Marca", unidad.marca],
              ["Modelo", unidad.modelo],
              ["Año", unidad.anio],
              ["Color", unidad.color],
              ["Tipo", nombreTipo(tipos, unidad.tipo)],
              ["VIN", unidad.vin, true],
            ]}
          />
        </Panel>

        <Panel titulo="Datos del envío" testId={RASTREO.shippingCard}>
          <Datos
            filas={[
              ["Origen", unidad.datos_envio.origen],
              ["Destino", unidad.datos_envio.destino],
              ["Puerto de origen", unidad.datos_envio.puerto_origen],
              ["Puerto de destino", unidad.datos_envio.puerto_destino],
              ["Buque", unidad.datos_envio.buque],
              ["Entrega estimada", formatFecha(unidad.datos_envio.fecha_estimada_entrega)],
            ]}
          />
        </Panel>
      </div>

      {fotos && fotos.length > 0 && (
        <Panel titulo="Registro fotográfico" className="mt-5">
          <div className="pt-2">
            <Gallery fotos={fotos} />
          </div>
        </Panel>
      )}

      {videos && videos.length > 0 && (
        <Panel titulo="Videos del vehículo" testId={RASTREO.videosPanel} className="mt-5">
          <div className="grid gap-3 pt-3 sm:grid-cols-2">
            {videos.map((v) => <VideoItem key={v.id} video={v} />)}
          </div>
        </Panel>
      )}
    </main>
  );
}

function Panel({ titulo, children, className = "", testId }) {
  return (
    <section data-testid={testId} className={`rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6 tc-fade-up ${className}`}>
      <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--tc-text-secondary)]">{titulo}</h2>
      {children}
    </section>
  );
}

function Datos({ filas }) {
  return (
    <dl className="mt-3 divide-y divide-[var(--tc-border)]">
      {filas.map(([k, v, mono]) => (
        <div key={k} className="flex items-center justify-between gap-4 py-2.5">
          <dt className="text-sm text-[var(--tc-text-secondary)]">{k}</dt>
          <dd className={`text-right text-sm font-medium text-[var(--tc-text)] ${mono ? "font-mono text-xs tabular-nums" : "tabular-nums"}`}>
            {v || "—"}
          </dd>
        </div>
      ))}
    </dl>
  );
}
